// Stable workflow codes the automation relies on (data-model.md Section 7, DM-3, REQ-7.1, REQ-7.9).
// Names live in the database and can be renamed; these codes never change.
import { CODE_LISTS, type StageCode } from './code-lists.js';

/** One workflow per ticket type, with the same code as the type (poc CT-004, architecture 7.11). */
export const WORKFLOW_CODES = { bug: 'bug', featureRequest: 'feature_request' } as const;
export type WorkflowCode = (typeof WORKFLOW_CODES)[keyof typeof WORKFLOW_CODES];
export const DEFAULT_WORKFLOW_CODE: WorkflowCode = WORKFLOW_CODES.bug;

export const TICKET_TYPE_CODES = { bug: 'bug', featureRequest: 'feature_request' } as const;
export type TicketTypeCode = (typeof TICKET_TYPE_CODES)[keyof typeof TICKET_TYPE_CODES];

/** The workflow a ticket type follows. The identity map, kept as one function so the rule lives in one place. */
export function workflowCodeForType(type: TicketTypeCode): WorkflowCode {
  return type;
}

export const STAGE_CODES = CODE_LISTS.stage_code;
/**
 * The stage rows each workflow has. The stage codes stay the fixed list (REQ-7.1); the feature-request
 * workflow has no Awaiting confirmation stage, because a feature request has no confirm step (poc CT-004).
 */
export const WORKFLOW_STAGES: Record<WorkflowCode, readonly StageCode[]> = {
  bug: STAGE_CODES,
  feature_request: STAGE_CODES.filter((code) => code !== 'awaiting_confirmation'),
};
export const OPEN_STAGES: readonly StageCode[] = ['triage', 'review', 'ready', 'in_progress'];
export const FINISHED_STAGES: readonly StageCode[] = ['done', 'not_actioned'];

/**
 * statuses.system_code values: the statuses the automation names (REQ-7.9). They can't be deleted. Unique
 * per workflow: the feature-request workflow reuses the codes the commands name, under its own names.
 */
export const STATUS_SYSTEM_CODES = [
  'new',
  'needs_review',
  'accepted',
  'assigned',
  'in_progress',
  'fixed',
  'closed',
  'declined',
  'feature_request',
  'withdrawn_by_reporter',
  // Done / Shipped: the status Mark shipped targets, in the feature-request workflow only (poc CT-004)
  'shipped',
] as const;
export type StatusSystemCode = (typeof STATUS_SYSTEM_CODES)[number];

/** The ticket reference shown to people: `ticket.reference_prefix` + reference_number (data-model 13.2). */
export function formatTicketReference(prefix: string, referenceNumber: number): string {
  return `${prefix}-${referenceNumber}`;
}
